import React from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { Tooltip } from './ui/Tooltip';

interface FieldNameBadgeProps {
  /** The snake_case field identifier */
  fieldName: string;
  /** Whether this field is currently highlighted */
  isHighlighted?: boolean;
  /** Optional CSS class name for the wrapper */
  className?: string;
  /** Position variant - 'inline' for form fields, 'corner' for div sections */
  variant?: 'inline' | 'corner';
}

/**
 * FieldNameBadge - Shows a small info icon with tooltip displaying the field name
 * Used for LLM help integration to identify specific fields in the UI
 *
 * Features:
 * - Info icon with tooltip showing field name
 * - Glow effect when highlighted
 * - Minimal visual footprint
 * - Corner variant for tagging div sections (stat cards, panels, etc.)
 */
export const FieldNameBadge: React.FC<FieldNameBadgeProps> = ({
  fieldName,
  isHighlighted = false,
  className = '',
  variant = 'inline',
}) => {
  const cornerClasses = variant === 'corner'
    ? 'absolute top-1 right-1 z-10 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-full p-1'
    : '';

  return (
    <Tooltip content={fieldName} placement="top">
      <div
        data-field-name={fieldName}
        className={`${variant === 'inline' ? 'inline-flex' : ''} items-center justify-center ${cornerClasses} ${className}`}
      >
        <InformationCircleIcon
          className={`w-3.5 h-3.5 transition-all duration-300 ${
            isHighlighted
              ? 'text-amber-500 dark:text-amber-400 drop-shadow-[0_0_8px_rgba(245,158,11,0.8)] animate-pulse'
              : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'
          }`}
        />
      </div>
    </Tooltip>
  );
};

/**
 * FieldNameWrapper - Wraps a form field with dotted border when field names are visible
 * Shows info badge when enabled
 */
interface FieldNameWrapperProps {
  /** The snake_case field identifier */
  fieldName: string;
  /** Whether field names should be shown */
  showFieldNames: boolean;
  /** The field ID that is currently highlighted */
  highlightedFieldId: string | null;
  /** The label text for the field */
  label?: string;
  /** Children (the actual form field) */
  children: React.ReactNode;
  /** Optional CSS class name */
  className?: string;
}

export const FieldNameWrapper: React.FC<FieldNameWrapperProps> = ({
  fieldName,
  showFieldNames,
  highlightedFieldId,
  label,
  children,
  className = '',
}) => {
  const isHighlighted = highlightedFieldId === fieldName;

  return (
    <div
      data-field-name={fieldName}
      className={`relative ${className} ${
        showFieldNames
          ? 'border border-dashed border-gray-300 dark:border-gray-600 rounded-md p-2'
          : ''
      } ${
        isHighlighted
          ? 'ring-2 ring-amber-500 dark:ring-amber-400 shadow-[0_0_12px_rgba(245,158,11,0.6)] animate-pulse'
          : ''
      }`}
    >
      {label && showFieldNames && (
        <div className="flex items-center gap-1 mb-1">
          <label className="text-xs font-medium text-label">
            {label}
          </label>
          <FieldNameBadge
            fieldName={fieldName}
            isHighlighted={isHighlighted}
          />
        </div>
      )}
      {children}
    </div>
  );
};

/**
 * FieldNameSection - Wraps an entire div/section (stat card, panel, filter, etc.)
 * Shows corner badge and flash animation when highlighted
 */
interface FieldNameSectionProps {
  /** The kebab-case section identifier (e.g., 'per-diem-not-dispatched') */
  fieldName: string;
  /** Whether field names should be shown */
  showFieldNames: boolean;
  /** The field ID that is currently highlighted */
  highlightedFieldId: string | null;
  /** Children (the actual section content) */
  children: React.ReactNode;
  /** Optional CSS class name */
  className?: string;
}

export const FieldNameSection: React.FC<FieldNameSectionProps> = ({
  fieldName,
  showFieldNames,
  highlightedFieldId,
  children,
  className = '',
}) => {
  const isHighlighted = highlightedFieldId === fieldName;

  return (
    <div
      data-field-name={fieldName}
      className={`relative ${className} ${
        isHighlighted
          ? 'ring-4 ring-amber-500 dark:ring-amber-400 shadow-[0_0_20px_rgba(245,158,11,0.8)] animate-[pulse_1s_ease-in-out_3]'
          : ''
      }`}
    >
      {showFieldNames && (
        <FieldNameBadge
          fieldName={fieldName}
          isHighlighted={isHighlighted}
          variant="corner"
        />
      )}
      {children}
    </div>
  );
};

export default FieldNameBadge;
